import {
  FileButton,
  Space,
  Stack,
  Loader,
  Divider,
  Group,
  Avatar,
  Badge,
} from "@mantine/core";
import { Dropzone, PDF_MIME_TYPE } from "@mantine/dropzone";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button } from "@/components/ui/Button";
import { AdvizeApi } from "@/api/api";
import {
  IconArrowDown,
  IconExternalLink,
  IconRefresh,
  IconUpload,
  IconX,
} from "@tabler/icons-react";
import { Title } from "@/components/ui/Title";
import { useStyles } from "@/styles/useStyles";
import { Text } from "@/components/ui/Text";
import { Link } from "@/components/ui/Link";
import { config } from "@/config";
import { DVDStyleLoaderOverlay } from "@/components/playlists/DVDStyleLoaderOverlay";
import { Experience } from "@/services/Experience";
import {
  useActiveUser,
  useAdvizer,
  useAdvizerQuotes,
  useQuestions,
} from "@/store/selectors";
import { AdvizerVideo } from "@/components/AdvizerVideo";
import { AdvizerTag } from "@/components/AdvizerTag";
import { AdvizerQuotes } from "@/components/AdvizerQuotes";
import { AssignmentService } from "@/services/AssignmentService";
import { useActiveAssignment } from "@/store/selectors/assignmentSelectors";
import { Analytics } from "@/services/Analytics";

const USE_STATIC_DATA = false;

export function PDFRecs() {
  const { theme } = useStyles();
  const [isLoading, setIsLoading] = useState(false);
  const [recData, setRecData] = useState<any>(
    USE_STATIC_DATA ? getStaticRecs() : null,
  );
  console.log({ recData });
  const [isHoveredDropzone, setIsHoveredDropzone] = useState(false);
  //   const [previewURL, setPreviewURL] = useState<string | null>(
  //     "blob:http://localhost:5173/96fec699-8748-49b1-a7e7-ff1217a68272",
  //   );
  const containerRef = useRef<HTMLDivElement>(null);
  const [totalClicks, setTotalClicks] = useState(0);
  const activeAssignment = useActiveAssignment();
  const activeUser = useActiveUser();

  const tagsByCategory = useMemo(() => {
    const themeColors = [
      "pink",
      "cyan",
      "teal",
      "grape",
      "indigo",
      "yellow",
      "orange",
      "blue",
      "green",
      "red",
      "purple",
      "gray",
      "brown",
      "lime",
    ];
    const map: Record<string, any> = {};
    recData?.tags?.tags?.forEach((tag: any, index: number) => {
      if (!map[tag.category]) {
        map[tag.category] = {
          tags: [],
          color: themeColors[Object.keys(map).length % themeColors.length],
        };
      }

      map[tag.category].tags.push({
        ...tag,
        index,
        color: map[tag.category].color,
      });
    });
    return map;
  }, [recData?.tags?.tags]);

  const tagsMap = useMemo(() => {
    const map: Record<string, any> = {};
    Object.keys(tagsByCategory).forEach((category) => {
      tagsByCategory[category].tags.forEach((tag: any) => {
        map[tag.tag] = {
          ...tag,
          // category,
          color: tagsByCategory[category].color,
        };
      });
    });
    return map;
  }, [tagsByCategory]);

  const tagsByAdvizer = useMemo(() => {
    const map: Record<string, any> = {};
    recData?.results?.forEach((result: any) => {
      result?.rankedTranscriptsWithTags?.forEach((option: any) => {
        const optionVideoData = option.videoData;
        (option.tags || []).forEach((tag: any) => {
          // map[optionVideoData.advizerId] = {}
          if (!map[optionVideoData.advizerId]) {
            map[optionVideoData.advizerId] = {};
          }

          if (!map[optionVideoData.advizerId][tag]) {
            map[optionVideoData.advizerId][tag] = 0;
          }

          map[optionVideoData.advizerId][tag]++;
        });
      });
    });

    return map;
  }, [recData]);

  const tagsByQuestionForPlaylistAdvizers = useMemo(() => {
    const map: Record<string, any> = {};
    recData?.results?.forEach((result: any) => {
      result?.rankedTranscriptsWithTags?.forEach(
        (option: any, index: number) => {
          if (index > 2) {
            return;
          }

          if (!map[result.questionNumber]) {
            map[result.questionNumber] = {};
          }

          option.tags.forEach((tag: any) => {
            if (!map[result.questionNumber][tag]) {
              map[result.questionNumber][tag] = 0;
            }
            map[result.questionNumber][tag]++;
          });
        },
      );
    });

    return map;
  }, [recData]);

  useEffect(() => {
    if (recData) {
      localStorage.setItem("recData", JSON.stringify(recData));
    }

    // console.log({ recData });
  }, [recData]);

  useEffect(() => {
    const recData = localStorage.getItem("recData");
    if (recData) {
      setRecData(JSON.parse(recData));
      setTotalClicks(0);
    }
  }, []);

  const handleFileChange = useCallback(
    async (file: File | null) => {
      setIsLoading(true);
      if (!file) return;
      const formData = new FormData();
      formData.append("file", file);

      // if (file && file.type === "application/pdf") {
      //   const url = URL.createObjectURL(file);
      //   setPreviewURL(url);
      // }

      Analytics.getRecsFromPdf({
        assignmentId: activeAssignment?.id,
        assignmentTitle: activeAssignment?.title,
        page: "PDF Recs",
        context: activeAssignment?.uploadType,
      });

      let response = null;
      if (USE_STATIC_DATA) {
        await new Promise((resolve) => setTimeout(resolve, 5000));
        response = getStaticRecs();
      } else {
        response = await AdvizeApi.getRecsFromPdf(formData);
      }

      Experience.showConfetti();

      if (activeAssignment?.id && activeUser?.id) {
        AssignmentService.updateUserAssignmentResponse({
          assignmentId: activeAssignment?.id,
          studentUserId: activeUser?.id,
          promptId: "careerLeader",
          response: "true",
        });
      }

      setRecData(response);
      setIsLoading(false);
    },
    [
      activeAssignment?.id,
      activeAssignment?.title,
      activeAssignment?.uploadType,
      activeUser?.id,
    ],
  );

  const renderUploadButton = useCallback(() => {
    const iconSize = 42;
    return (
      <Dropzone
        onDrop={(files) => handleFileChange(files[0])}
        onReject={(files) => console.log("rejected files", files)}
        maxSize={5 * 1024 ** 2}
        accept={PDF_MIME_TYPE}
        style={{
          border: `1px dashed ${isHoveredDropzone ? theme.colors.primary[5] : theme.colors.border[4]}`,
          borderRadius: 4,
          boxShadow: theme.shadows.sm,
          cursor: "pointer",
        }}
        p="xl"
        py={40}
        w="100%"
        maw={500}
        bg="background.1"
        onMouseEnter={() => setIsHoveredDropzone(true)}
        onMouseLeave={() => setIsHoveredDropzone(false)}
        onDragEnter={() => setIsHoveredDropzone(true)}
        onDragLeave={() => setIsHoveredDropzone(false)}
      >
        <Stack
          justify="center"
          align="center"
          gap="sm"
          style={{ cursor: "pointer" }}
        >
          <Dropzone.Accept>
            <IconUpload
              size={iconSize}
              color="var(--mantine-color-blue-6)"
              stroke={1.5}
            />
          </Dropzone.Accept>
          <Dropzone.Reject>
            <IconX
              size={iconSize}
              color="var(--mantine-color-red-6)"
              stroke={1.5}
            />
          </Dropzone.Reject>
          <Dropzone.Idle>
            <IconUpload
              size={iconSize}
              color={
                isHoveredDropzone
                  ? theme.colors.primary[5]
                  : theme.colors.text[6]
              }
              stroke={1.5}
            />
          </Dropzone.Idle>

          <Stack>
            <Text size="xl" inline titleStyle="h4" c="text.7" fw={500}>
              Drag your PDF here or click to select it
            </Text>
          </Stack>
        </Stack>
      </Dropzone>
    );
    return (
      <FileButton onChange={handleFileChange}>
        {(props) => (
          <Button
            {...props}
            leftSection={<IconUpload size={20} />}
            size="md"
            radius="xl"
            maw={400}
            color="primary"
            fullWidth
          >
            Upload CareerLeader Results
          </Button>
        )}
      </FileButton>
    );
  }, [
    handleFileChange,
    isHoveredDropzone,
    theme.colors.border,
    theme.colors.primary,
    theme.colors.text,
    theme.shadows.sm,
  ]);

  const renderLoading = useCallback(() => {
    return (
      <Stack
        mx="auto"
        w="100%"
        maw={600}
        pt={180}
        justify="center"
        align="center"
        gap={0}
      >
        <Loader size="lg" color={theme.colors.primary[5]} />
        <Space h="md" />
        <Title order={1} size="h2" ta="center" lh={1.2} c="text.8" fw={700}>
          Sit tight! Videos are on their way.
        </Title>

        <Space h="xl" />
        <Divider w="100%" />
        <Space h="xl" />

        <Text size="md" titleStyle="h3" ta="center" c="primary.7" maw={500}>
          How many times can you click the ball?
        </Text>
        <Space h="xs" />
        <Text titleStyle="h1" ta="center" c="primary.8" maw={500}>
          {totalClicks} clicks
        </Text>
        <Stack
          ref={containerRef}
          h={300}
          w={300 * (16 / 9)}
          style={{
            position: "relative",
            border: `1px solid ${theme.colors.border[3]}`,
            borderRadius: 4,
          }}
        >
          <DVDStyleLoaderOverlay
            containerRef={containerRef}
            onClickBall={() => {
              setTotalClicks((totalClicks) => totalClicks + 1);
            }}
          />
        </Stack>
        <Space h="xl" />
        <Text size="sm" ta="center" c="text.7" maw={500}>
          Having issues? Email support at dev@advizehub.com
        </Text>
      </Stack>
    );
  }, [theme.colors.border, theme.colors.primary, totalClicks]);

  const renderRestart = useCallback(() => {
    return (
      <Stack>
        <Text titleStyle="h2" ta="center" lh={1.2} fw={700}>
          Want to try again?
        </Text>
        <Button
          leftSection={<IconRefresh size={20} />}
          color="primary"
          onClick={() => {
            setRecData(null);
            localStorage.removeItem("recData");
            setTotalClicks(0);
            Analytics.retryRecsFromPdf({
              assignmentId: activeAssignment?.id,
              assignmentTitle: activeAssignment?.title,
              page: "PDF Recs",
              context: activeAssignment?.uploadType,
            });
            // window.location.reload();
          }}
        >
          Restart
        </Button>
      </Stack>
    );
  }, [
    activeAssignment?.id,
    activeAssignment?.title,
    activeAssignment?.uploadType,
  ]);

  const renderContent = useCallback(() => {
    if (isLoading) {
      return renderLoading();
    }

    if (recData) {
      return (
        <Stack pt={100} pb={100} align="center" justify="center" w="100%">
          <Recommendations
            recData={recData}
            tagsByCategory={tagsByCategory}
            tagsMap={tagsMap}
            tagsByAdvizer={tagsByAdvizer}
            tagsByQuestionForPlaylistAdvizers={
              tagsByQuestionForPlaylistAdvizers
            }
          />
          <Space h="xl" />
          {renderRestart()}
        </Stack>
      );
    }

    return (
      <Stack
        mx="auto"
        w="100%"
        maw={600}
        pt={200}
        justify="center"
        align="center"
        gap="xs"
      >
        <Title order={1} size="h2" ta="center" lh={1.2}>
          Upload your CareerLeader results and{" "}
          <span style={{ color: theme.colors.primary[7] }}>
            get the perfect career insight videos for you
          </span>
        </Title>

        <Text size="sm" ta="center" c="text.8" maw={500}>
          In less than 30 seconds, we'll analyze your CareerLeader results,
          search our 10,000+ videos and find the most helpful content for you,
          so that you can go deep on relevant career paths, day in the life,
          important skills, biggest challenges and more.
        </Text>
        <Space h="xs" />
        {renderUploadButton()}

        <Space h="xl" />
        <Text size="xs" ta="center" c="text.5" maw={400}>
          When you upload your file, we will not store it, but we will use third
          party AI models to analyze it and find the most relevant content for
          you.
        </Text>
        {/* <Space h="xs" /> */}
        <Text size="xs" ta="center" c="text.5" maw={400}>
          And by using this service,
          <br /> you agree to our{" "}
          <Link to={config.termsUrl} style={{ opacity: 0.8 }}>
            Terms of Service
          </Link>{" "}
          and{" "}
          <Link to={config.privacyUrl} style={{ opacity: 0.8 }}>
            Privacy Policy
          </Link>
          .
        </Text>
      </Stack>
    );
  }, [
    isLoading,
    recData,
    renderLoading,
    renderRestart,
    renderUploadButton,
    tagsByAdvizer,
    tagsByCategory,
    tagsByQuestionForPlaylistAdvizers,
    tagsMap,
    theme.colors.primary,
  ]);

  return (
    <Stack h="100vh" style={{ overflow: "auto" }} w="100%" px="md">
      {renderContent()}
    </Stack>
  );
}

function ArrowDivider() {
  const { theme } = useStyles();

  return (
    <>
      <Space h={50} />
      <IconArrowDown size={32} color={theme.colors.text[7]} />
      <Space h={50} />
    </>
  );
}

function Recommendations(props: {
  recData: any;
  tagsByCategory: any;
  tagsMap: any;
  tagsByAdvizer: any;
  tagsByQuestionForPlaylistAdvizers: any;
}) {
  const {
    recData,
    tagsByCategory,
    tagsMap,
    tagsByAdvizer,
    tagsByQuestionForPlaylistAdvizers,
  } = props;

  const renderTagOverview = useCallback(() => {
    return (
      <Stack gap="lg" w="100%" maw={800} px="xl">
        {Object.entries(tagsByCategory).map(
          ([category, tags]: [string, any], index: number) => (
            <Group key={index} align="flex-start" justify="space-between">
              <Text
                titleStyle="h5"
                ta="left"
                w={120}
                lh={1.2}
                style={{ textTransform: "capitalize" }}
              >
                {category}
              </Text>
              <Group flex={1} gap="xs">
                {tags.tags.map((tag: any, index: number) => (
                  <Badge
                    key={index}
                    color={tag.color}
                    size="lg"
                    variant="light"
                    style={{
                      textTransform: "capitalize",
                      fontWeight: 500,
                    }}
                  >
                    {tag.tag}
                  </Badge>
                ))}
              </Group>
            </Group>
          ),
        )}
      </Stack>
    );
  }, [tagsByCategory]);

  return (
    <Stack pt={50} gap={0} mx="auto" align="center" justify="center" w="100%">
      <Title order={1} size="h1" ta="center" lh={1.2}>
        Your recs are ready!
      </Title>

      <Stack mx="auto" w="100%" maw={600} gap={0}>
        <Text mx="auto" size="md" ta="center" c="text.7" lh={1.4} maw={600}>
          {recData.reasoning}
        </Text>
      </Stack>

      <Space h="xl" />

      {renderTagOverview()}

      <ArrowDivider />

      <Text titleStyle="h2" ta="center" lh={1.2} fw={700}>
        These are your {recData.recommendedAdvizers.length} best matching
        Advizers
      </Text>

      <Stack
        align="center"
        justify="center"
        w="100%"
        // wrap="nowrap"
        style={{ overflow: "auto" }}
        py="md"
        px="xl"
        maw={800}
      >
        {/* <Group
          w={singleAdvizerWidth * recData.recommendedAdvizers.length}
          wrap="nowrap"
        > */}
        {recData.recommendedAdvizers.map((advizer: any) => (
          <RecommendedAdvizer
            key={advizer.id}
            advizer={advizer}
            tagsMap={tagsMap}
            tagsByAdvizer={tagsByAdvizer}
          />
        ))}
        {/* </Group> */}
      </Stack>

      <ArrowDivider />

      <Text titleStyle="h1" ta="center" lh={1.2} fw={700}>
        Here are some playlists tailored to your CareerLeader results
      </Text>
      <Space h={50} />
      <Stack gap={80}>
        {recData.results
          .filter((playlistInfo: any) => {
            return [2, 3, 4, 5, 6].includes(playlistInfo.questionNumber);
          })
          .map((playlistInfo: any, index: number) => (
            <RecommendedPlaylists
              key={index}
              questionNumber={playlistInfo.questionNumber}
              arrayOfAdvizerOptions={playlistInfo.rankedTranscriptsWithTags}
              tagsMap={tagsMap}
              tagsByAdvizer={tagsByAdvizer}
              tagsByQuestionForPlaylistAdvizers={
                tagsByQuestionForPlaylistAdvizers
              }
            />
          ))}
      </Stack>

      <ArrowDivider />

      <Text titleStyle="h1" ta="center" lh={1.2} fw={700}>
        And here are some bonus playlists featuring your top Advizers
      </Text>
      <Space h={50} />
      <Stack gap={80}>
        {recData.results
          .filter((playlistInfo: any) => {
            return ![2, 3, 4, 5, 6].includes(playlistInfo.questionNumber);
          })
          .map((playlistInfo: any, index: number) => (
            <RecommendedPlaylists
              key={index}
              questionNumber={playlistInfo.questionNumber}
              tagsByQuestionForPlaylistAdvizers={
                tagsByQuestionForPlaylistAdvizers
              }
              arrayOfAdvizerOptions={
                playlistInfo.rankedTranscriptsWithTags || []
              }
              // tagsMap={tagsMap}
              // tagsByAdvizer={tagsByAdvizer}
            />
          ))}
      </Stack>
    </Stack>
  );
}

function RecommendedPlaylists(props: {
  arrayOfAdvizerOptions: any[];
  questionNumber: number;
  tagsMap?: any;
  tagsByAdvizer?: any;
  tagsByQuestionForPlaylistAdvizers?: any;
}) {
  const { arrayOfAdvizerOptions, questionNumber } = props;
  const { theme } = useStyles();
  const questions = useQuestions();
  const question = questions.find(
    (question) => question.number === questionNumber,
  );

  const questionNumberToTitle: Record<number, string> = useMemo(() => {
    return {
      1: "What is your career path up to now?",
      2: "What are your main responsibilities?",
      3: "What is a day in the life like for you?",
      4: "What are the most important skills for your role?",
      5: "What are your favorite parts of your role?",
      6: "What are the biggest challenges in your role?",
      7: "What are your favorite parts of your industry?",
      8: "Who thrives in your industry?",
      9: "What do you wish you'd known about your industry?",
      10: "What are some entry-level position ideas?",
      11: "What are significant lessons you've learned in your career?",
      12: "What are some college ideas for success?",
    };
  }, []);

  if (!question) {
    return null;
  }

  return (
    <Stack>
      <Text titleStyle="h2" ta="center" c="text.9" lineClamp={2} lh={1.2}>
        {/* <span style={{ color: theme.colors.text[6] }}>Q{questionNumber}: </span> */}
        <span style={{ color: theme.colors.primary[7], fontWeight: 700 }}>
          {questionNumberToTitle[Number(questionNumber)] || question.title}
        </span>
      </Text>
      {/* {tagsByQuestionForPlaylistAdvizers && tagsMap && (
        <>
          <Group align="center" justify="center" gap={8} maw={800} mx="auto">
            {Object.keys(
              (tagsByQuestionForPlaylistAdvizers || {})[questionNumber],
            ).map((tag) => {
              const color = tagsMap?.[tag]?.color || "gray";
              return (
                <Badge
                  key={tag}
                  color={color}
                  size="lg"
                  variant="light"
                  style={{ textTransform: "capitalize", fontWeight: 500 }}
                >
                  {tag}
                </Badge>
              );
            })}
          </Group>

          <Space h="sm" />
        </>
      )} */}

      <Group align="flex-start" justify="center">
        <Space h="sm" />
        {arrayOfAdvizerOptions.slice(0, 3).map((option, index) => (
          <PlaylistItem
            key={index}
            videoData={option.videoData}
            tags={option.tags}
          />
        ))}
      </Group>
    </Stack>
  );
}

function PlaylistItem(props: { videoData: any; tags: any }) {
  const videoData = props.videoData || {};
  const { theme } = useStyles();
  const advizer = useAdvizer(videoData.advizerId);
  const quotes = useAdvizerQuotes(videoData.advizerId);

  const advizerIsMBA = advizer?.gradDegree === "MBA";

  const videoRecord = advizer?.videos.find(
    (video: any) => video.vimeoId === videoData.vimeoId,
  );

  const matchingQuote = useMemo(() => {
    if (!quotes) {
      return undefined;
    }
    return quotes.find((quote) => quote.videoId === videoRecord?.id);
  }, [quotes, videoRecord?.id]);

  const renderTags = useCallback(() => {
    return null;
    return (
      <Stack gap="xs" w="100%" opacity={0.75}>
        <Group w="100%" gap={8} px={0} py="xs">
          {advizerIsMBA && (
            <AdvizerTag
              key="mba"
              tag={{ id: "MBA", title: "MBA", type: "degree" }}
              size="sm"
              maw={300}
              index={0}
              disableClick
            />
          )}
          {advizer?.tags.map((tag, index) => (
            <AdvizerTag
              key={tag.id}
              tag={tag}
              size="sm"
              maw={300}
              index={index}
              disableClick
            />
          ))}
        </Group>
      </Stack>
    );
  }, [advizer?.tags, advizerIsMBA]);

  const renderQuote = useCallback(() => {
    if (!advizer) {
      return null;
    }
    return (
      <AdvizerQuotes
        advizer={advizer}
        activeQuote={matchingQuote}
        quoteFontSize={14}
      />
    );
  }, [advizer, matchingQuote]);

  const renderAdvizerTitle = useCallback(() => {
    if (!advizer) {
      return null;
    }
    return (
      <Text titleStyle="h4" ta="left" c="text.9" lineClamp={2} lh={1.2}>
        {advizer.jobTitle} @{" "}
        <span style={{ fontWeight: 700, color: theme.colors.text[7] }}>
          {advizer.publicCompanyName}
        </span>
      </Text>
    );
  }, [advizer, theme.colors.text]);

  if (!videoRecord) {
    return null;
  }

  return (
    <Stack
      w={350}
      justify="flex-start"
      align="flex-start"
      gap={0}
      style={{ borderRadius: 8, overflow: "hidden" }}
    >
      <AdvizerVideo
        // advizerId={videoData.advizerId}
        videoId={videoRecord.id}
        url={videoRecord.url}
        video={videoRecord}
        hideDescriptors
      />
      <Space h="sm" />
      {renderAdvizerTitle()}
      <Space h={4} />
      {renderQuote()}
      {renderTags()}
    </Stack>
  );

  return (
    <Stack>
      <Text>{videoData.transcript}</Text>
    </Stack>
  );
}

function RecommendedAdvizer(props: {
  advizer: any;
  tagsMap: Record<string, any>;
  tagsByAdvizer: Record<string, any>;
}) {
  const { advizer: advizerData, tagsMap } = props;
  const advizer = useAdvizer(advizerData.id);
  const [hovered, setHovered] = useState(false);
  const { theme } = useStyles();

  const topTagsForAdvizer = useMemo(() => {
    return advizerData.tags;
    // const tags = tagsByAdvizer[advizerData.id] || {};
    // return Object.entries(tags || {})
    //   .sort((a: any, b: any) => b[1] - a[1])
    //   .slice(0, 5)
    //   .map(([tag]) => tag);
  }, [advizerData.tags]);

  if (!advizer) {
    return null;
  }

  return (
    <Button
      unstyled
      to={`/advizers/${advizer.id}`}
      target="_blank"
      px="xl"
      py="xl"
      mx="auto"
      w="100%"
      // w={singleAdvizerWidth}
      pos="relative"
      style={{
        boxShadow: theme.shadows.sm,
        border: hovered
          ? `1px solid ${theme.colors.primary[3]}`
          : `1px solid ${theme.colors.border[3]}`,
        borderRadius: theme.radius.md,
        position: "relative",
        // width: "100%",
      }}
      linkStyle={{
        width: "100%",
      }}
      radius="md"
      color={hovered ? "primary" : "gray"}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {/* <ActionIcon
        ariaLabel="View profile"
        variant="transparent"
        size="xs"
        pos="absolute"
        top={8}
        right={8}
      > */}
      <Group
        pos="absolute"
        top={theme.spacing.xs}
        right={theme.spacing.xs}
        w="100%"
        justify="flex-end"
      >
        <IconExternalLink
          aria-label="View profile"
          style={{
            // position: "absolute",
            // top: 8,
            // right: 8,
            color: theme.colors.primary[7],
            maxWidth: 20,
          }}
        />
      </Group>
      {/* </ActionIcon> */}

      <Group w="100%" align="center" justify="flex-start">
        <Avatar
          src={advizer?.profilePictureURL}
          size="xl"
          style={{ border: `2px solid ${theme.colors.primary[7]}` }}
        />
        <Stack gap={0} flex={1} ta="left">
          <Text titleStyle="h5" ta="left" c="text.6" lineClamp={1}>
            {advizer?.firstName}
          </Text>
          <Space h={2} />
          <Text titleStyle="h3" ta="left" c="text.8" lineClamp={2} lh={1.2}>
            {advizer?.jobTitle} @ {advizer?.publicCompanyName}
          </Text>
        </Stack>
      </Group>
      <Stack gap={0}>
        <Space h="md" />
        <Group gap={8} px={0}>
          {topTagsForAdvizer.map((tag: any, index: number) => (
            <Badge
              key={index}
              size="lg"
              variant="light"
              color={tagsMap[tag]?.color || "gray"}
              style={{
                textTransform: "capitalize",
                fontWeight: 500,
              }}
            >
              {tag}
            </Badge>
          ))}
        </Group>
        <Space h="sm" />
        <Text size="md" ta="left" c="text.7" lineClamp={4} lh={1.2} px="xs">
          {advizerData?.reasoning}
        </Text>
      </Stack>
    </Button>
  );
}

function getStaticRecs() {
  return {
    results: [
      {
        questionNumber: 1,
        videoDataForTop3Transcripts: [
          {
            vimeoId: "1032520644",
            advizerId: "a30d12e3-b19c-4165-9be9-2d7567c8d6db",
            transcript:
              "Could you walk me through your career path, starting with your experiences in college, any internships or jobs that you've had before your current role? Yeah. For, uh, for my MAs, uh, for my bachelor degree, I studied computer information system. And after I graduated from my bachelor degree, I studied master, um, and major in business analytics. I also like learning machine learning, ai, and after graduating from master I go to two different company words, business intelligence analyst and also product analyst. And after these two job experience, I start my own company's coor. So we build, um, a platform to help a lot of sports organization to break down video into stats with ai. Yep. That's my career path.",
          },
          {
            vimeoId: "900227427",
            advizerId: "5b060460-7ca6-4247-b4e1-aebb6e4af4b6",
            transcript:
              "Could you walk me through your career path, starting with your experiences in college, any internships or jobs you had before your current role? Yeah, sure thing. Uh, so, uh, prior to coming outta college, um, I was a student athlete. Uh, I attended Duke University. Uh, my career path is pretty straightforward. Um, my, my experience to date, I spent four and a half years in consulting at Deloitte. Um, prior to joining that, uh, the summer before I, uh, graduated from college, I took on a consulting role with a boutique consultancy that was focused on the public sector, uh, in Washington, dc. From there, I used that as a launching pad, kind of into a, a larger consulting firm. Um, and I went through the standard recruiting route, uh, out of, out of undergrad. And, uh, joined Deloitte, uh, shortly after graduation and spent, uh, I've spent the last four and a half years there, um, uh, through working through a couple promotions and kind of working my way up the, uh, consulting ladder.",
          },
          {
            vimeoId: "836288992",
            advizerId: "08b417e7-e236-4ad8-be68-bc16e5c1d187",
            transcript:
              "Could you walk me through your career path, starting with your experiences in college, any internships or jobs you had before your current role? Yeah, absolutely. So I got my degrees in finance and accounting with a minor in sociology. And initially was a marketing and finance major. Thought I'd be in sales, but sometimes life just takes you a different path. And, uh, started my career at pwc, got my c p a license. Really enjoyed the client engagement aspect of it. Not so much the auditing side, however, it was a great platform, great door opener. And then from there, um, I went into finance and strategy, worked in a variety of different roles, including operations. Sort of realized that, that the big corporate world wasn't for me. It wasn't really a match that I really excelled more in, um, smaller and medium sized businesses. And, you know, current day I work primarily, um, with small businesses as a business owner and then also serving small businesses with clients and that's really my bread and butter. Um, but as far as internships, um, I did intern with, um, PWC and Deloitte and it was great experience. Um, I really learned so much. I think surrounding yourselves with really smart people who are willing to teach you, um, and being open-minded and asking questions and learning as much as you can, as well as, um, seeing where you can add value is part of what you need to do to create the experience. You can't just rely on somebody else to give you the experience. You have to really cultivate what kind of experience that you wanna get and just being a sponge, really just absorbing everything that you possibly can and staying humble and learning. Um, I've been out of college for years now and I'm still every day learning something, so I think the education piece never stops. I think you have to always continue continuously be educating yourself on whatever that is, whether it's something work related or it's a personal interest. Um, I think that's extremely important.",
          },
        ],
        top3Transcripts: [5, 6, 10],
        reasoning:
          "I looked for transcripts of professionals whose career paths involved a mix of business, entrepreneurship, and leadership, with a bias towards people who went to business school. Transcript 5 is good because it is a computer information system student, studied business analytics, and then started his own company. Transcript 6 is good because this person also went to Duke, attended Deloitte and had a consulting role. Transcript 10 is good because this person got degrees in finance and accounting, which fits the bill.",
      },
      {
        questionNumber: 2,
        videoDataForTop3Transcripts: [
          {
            vimeoId: "878803043",
            advizerId: "061c1099-ff72-42b1-8337-81446490f56f",
            transcript:
              "What are your main responsibilities within your current role? So as a founder, I really work to scale our company, but beyond that, some of my main responsibilities have to do with cultivating and maintaining our key partnerships, whether that be with nonprofit organizations, maybe high profile celebrities and athletes, or we work with a number of agencies and corporate partners as well. So what Parachute does is work to help nonprofits form genuine partnerships with celebrity ambassadors to help their fundraising efforts, but also their awareness initiatives. So it's a lot of fun matchmaking, but also vetting the different partners and then strategizing around a specific campaign and then of course, project management. But a lot of it is impact focus work, which is really enjoyable. I.",
          },
          {
            vimeoId: "866168550",
            advizerId: "90d873be-e874-4ed7-902e-d52cab20a797",
            transcript:
              "What are your main responsibilities within your current role? Sure. So I'm the c e o of my business head of happiness, which we are on a mission to make individuals and organizations happier and healthier through the science of happiness. So my current responsibilities, one is I do a lot of coaching, so that's working with clients to help 'em get unstuck, help 'em really figure out what is important to 'em, what lights them up, and expand their possibility of possibilities and help 'em get there. Um, two, as business development, I'm constantly putting myself in networks, networking situations, going to events, um, reaching out to people on LinkedIn, looking for new potential clients. And then there's also, I do corporate or organizational work as well, so going into corporations or schools leading workshops. So there's the element of doing that. There's also the element of preparing for those, building my workshops, building my, building, my content, and, um, and yeah, and then doing it so.",
          },
          {
            vimeoId: "811083287",
            advizerId: "653db63f-b5e6-456b-bb2e-2c946b4dd65f",
            transcript:
              "What are your main responsibilities in your current role? Sure, so I own my own. Companies owner I am an executive. Trainer and so I work with individuals in a one-on-one coaching capacity professional coach and then also in a group facilitation standpoint so I can work in offsites Retreats work with big teams to develop high performance. That could be presentation skills. It could be coaching and feedback personality assessments. How do we communicate better work better with one another and so in order to do all of that and run my own business and say a lot is the back end business management. So invoicing in Insurance making sure that booking my own travel if I'm honest there's a lot that's Biz death. So the networking the relationship building the submitting for rfps pitching my business and my services there's the content creation. So if I book a gig what am I actually going to be presenting to them or sharing with them and then I'd say lastly is actual execution of it all so I spend probably about 10 to 20% of my time traveling where I am working with clients on the road at their offices or an offsite locations. And then if it's one-on-one coaching, I am engaging with them usually over Zoom or via phone. So there's a lot of variety in I think running a small business but also in my line of work where again I work with large groups and then also individuals",
          },
        ],
        top3Transcripts: [1, 9, 8],
        reasoning:
          "The CareerLeader report highlights interests in coaching/mentoring, influencing others, and skills in interpersonal effectiveness and leadership. The report also suggests career matches in non-profit management and training/organizational development. Considering these factors, I prioritized transcripts that discussed roles involving business development for a cause or company and that included coaching, relationship building, and leadership responsibilities. Transcript 1 discusses working with nonprofits and celebrities which would be helpful to know how to do and the impact that it has. Transcript 9 discusses coaching, networking and leading workshops which is very helpful. Transcript 8 includes professional coaching and group facilitation which is a good fit and the discussion of back end business management and Biz Dev is also related to the career matches.",
      },
      {
        questionNumber: 3,
        videoDataForTop3Transcripts: [
          {
            vimeoId: "878803079",
            advizerId: "061c1099-ff72-42b1-8337-81446490f56f",
            transcript:
              "What does a day in the life of a founder look like? Personally, I like to start my day off with responding to emails and following up on anything that's needed to effectively start that new workday. Uh, this is not the same for everyone, but I block out time for this in the morning because it really allows me to check a number of things off of my to-do list in that quick 30 minute, you know, first window of my day, which sets the tone for me to, you know, feel very productive for the rest of the day. But once I'm done with my emails, I typically dive right into different meetings with our partners, so again, celebrities and nonprofits, building on some of our current relationships and different projects, but also reaching out to new potential partners and collaborators. So I would say outreach is really about 98% of what I do on a day-to-Day basis, because it's such an important part of our company and our success. But there's also a lot of, you know, communication and collaboration with some of our other departments just to make sure we're all working together. We're all on the same page and running like a well-oiled machine. And as founder, I also work with a lot of our investors and our board members. So weekly meetings, sometimes multiple meetings each week just to provide progress updates and general check-ins, which can be a big part of any startup.",
          },
          {
            vimeoId: "866168577",
            advizerId: "90d873be-e874-4ed7-902e-d52cab20a797",
            transcript:
              "What does a day in the life of a founder and c e o look like? Yeah, great question, Emily. So, honestly, every single day is different. You know, I try to block my days with similar work. So rather than bouncing from activities to activity every day I try to do, Mondays is my creative days, and that's when I do a lot of my writing, a lot of my, uh, content generation. Um, Tuesdays and Wednesdays tend to be my coaching days. Um, Thursdays and Fridays are whatever needs to happen, but I try to be as intentional as possible with during like specific days for specific types of work. And then through sprinkle, throughout that, I'll do a lot of morning coffees, a lot of, uh, evening get togethers to really continue to build out the brand and network is as the c e o and founder. I am the face of the company and I am the one who, uh, really brings, brings business in.",
          },
          {
            vimeoId: "889305616",
            advizerId: "4bc9ef66-1b6b-4c25-a1dc-f408d2afd3eb",
            transcript:
              "What does a day in the life of a founder look like? I usually start my day combing through my emails and then responding to what I can on the spot. Um, and then what I like to do with my week that I started not that long ago is I like to plan certain days of the week for certain departments, if you will, because I handle, um, everything and all departments. If I were to try and go from task to task, from department to depart department throughout the day, I found that I got really overwhelmed. So I like to kind of block out Tuesdays to have my calls. Like I'll do Tuesday, Thursdays and I'll try and do all my calls on those two days. And then, you know, I'll work on cold outreach on Wednesdays and I'll do, so I kind of try and organize my week, um, and my, my time by department and so I can be more focused in my efforts and not get so overwhelmed with, you know, everything and my, my long to-do list of tasks. I.",
          },
        ],
        top3Transcripts: [1, 9, 2],
        reasoning:
          "Based on Emily's CareerLeader report, entrepreneurship and general management are strong career matches. Additionally, interests in coaching/mentoring and skills in interpersonal effectiveness, particularly empathy and teaching, are high. Therefore, the transcripts relating to founders or CEOs with descriptions focused on relationship building, communication, and time management are most relevant. Transcript 1 discusses outreach and relationship building, which aligns with the report. Transcript 9 emphasizes intentional time blocking for different types of work and building the brand, and Transcript 2 mentions planning and organization to avoid feeling overwhelmed, both of which align well with her skill set and potential watch-outs.",
      },
      {
        questionNumber: 4,
        videoDataForTop3Transcripts: [
          {
            vimeoId: "878803134",
            advizerId: "061c1099-ff72-42b1-8337-81446490f56f",
            transcript:
              "What skills are most important for a job like yours? Someone in my position needs to have strong communication skills, but also confidence. You really have to be able to pick up the phone or walk into a room and talk to anyone and everyone in that room. And of course, in order to do that, you need to have some confidence as well. But beyond confidence and communication, I would also add project management and problem solving to that list. Sometimes I have literally a dozen things going on at one time. Multiple campaigns, projects happening all over the world, different time zones, and if you're not able to manage both your time and your resources, you're going to run into some major problems with that. So communication, confidence, but also project management and problem solving.",
          },
          {
            vimeoId: "900227563",
            advizerId: "5b060460-7ca6-4247-b4e1-aebb6e4af4b6",
            transcript:
              "What skills are most important for a job like yours? Sure. I think, uh, consulting kind of gets a wrap as being very numbers heavy. And while there are certainly a, a lot of quantitative skills, um, and, you know, like I mentioned, data analysis needed, one of the things that's highly overlooked is kind of the people and the soft skills, um, that are being super critical to the role. And so I would say some of the most important skills from just a more of those soft skills and people related things include teamwork, uh, communication, um, and time management. Uh, you know, in in, in in consulting roles, you're, you're typically strapped for time. You're operating under traditional, usually under tight, fairly tight timelines, um, based on, you know, kind of client requests. Uh, and then on the other side of things, some of the more tactical, like hard skills. Um, you know, data analysis is certainly, uh, something that we do, uh, pretty frequently, whether that's, you know, financial data, um, performance data, you name it. Um, another thing that we'll look at, uh, and have to do quite frequently is we're working in PowerPoint. Um, so building presentations, kind of putting, being able to put its story together and, and tell that story in a, in a logical manner from end-to-end and, and really to any audience so they can digest it very easily is another key skill. Um, but I would say those are the two most kind of tactical things in addition to some of those, uh.",
          },
          {
            vimeoId: "937999505",
            advizerId: "27e2b536-49c3-4c03-821e-cbe15504cec7",
            transcript:
              "What skills are most important for a job like yours? I think being in business development, first and foremost, I really have to have, um, strong interpersonal skills. I need to be able to represent my company and talk about my company in an articulate way. Um, I need to be able to build relationships with people. So, you know, I have to be comfortable walking up to someone I don't know and building a relationship with them. I need to be able to go to trade shows and conferences. Um, again, not knowing anyone and being able to build the relationships. So I think interpersonal skills is very important. Um, you know, the big part of my business is, or my job is analyzing businesses, so I need to have a solid, um, analytical foundation. And so I need to understand, um, financial metrics. I need to understand macro and micro, uh, trends that are going on in specific industries. Whether those are positive tailwinds or negative headwinds. Um, I need to understand how to read, um, financial documentation. So understanding a 10 K, understanding a 10 Q, knowing how to read a p and l, knowing how to read a balance sheet, a cashflow statement, um, those are, are important. And then critical thinking, you know, being able to look at one of these companies, read through their materials and analyze whether or not this is a good business. So being able to not just read what's on the page, but use that critical thinking to say, Hey, what's not on the page? What can this company be doing that it's not doing? What can we bring to the company to make the company stronger and position it better for growth? So I think critical thinking is really important. And then, you know, this is a fast-paced environment. So you have to just have intellectual curiosity. You have to wanna wake up every morning, learn about new industries, learn about new businesses, learn about new trends in different sectors. Um, because intellectual co curiosity, I think will keep you energized within the, the role.",
          },
        ],
        top3Transcripts: [1, 6, 14],
        reasoning:
          "Emily's CareerLeader report highlights strong interpersonal skills, leadership potential, and a need to develop her professional network. Transcript 1 emphasizes communication and confidence which aligns with the interpersonal effectiveness. Transcript 6 focuses on teamwork, communication, and time management, also touching on the importance of data analysis and presentation skills, aligning with her analytical strengths and the need to present ideas effectively. Transcript 14 emphasizes interpersonal skills, analytical skills, and critical thinking, directly relating to Emily's skills and career matches in business development, offering actionable insights for her professional development.",
      },
      {
        questionNumber: 5,
        videoDataForTop3Transcripts: [
          {
            vimeoId: "900227590",
            advizerId: "5b060460-7ca6-4247-b4e1-aebb6e4af4b6",
            transcript:
              "What do you enjoy most about being in your career role? I think for me personally, um, you know, this is very personal to myself, but I enjoy being in a people oriented business. And so consulting at its core is management and dealing with people, uh, day in, day out, um, you know, and, and being flexible and being able to deal with, deal with people. And so, uh, as someone who really gets a lot of energy from, from working with people and in being in a people facing position, um, that's one of the things that I enjoy most, uh, is kind of that, that building relationships with my team members, building relationships with, with, with clients. Um, and then the other thing that I would add on, just as an aside, is, uh, you know, consulting and in my experience, there's quite a bit of flexibility in the nature of your work and, and kind of how it varies. Um, you know, as I mentioned earlier, the consulting projects are anywhere from three to five months long on, on average. And so, you know, there's a lot of flexibility in terms of every, let's call it every three to five months, you're, you're changing projects. So you could have a, a different focus and a, and a and something new at working with a new team, a new group of people, you know, every quarter of the year. And so, um, I I, I enjoy that it's, there's kind of continuous learning involved. Um, and so those are some of the things I enjoy most.",
          },
          {
            vimeoId: "889307201",
            advizerId: "4bc9ef66-1b6b-4c25-a1dc-f408d2afd3eb",
            transcript:
              "What do you enjoy most about being in your current role? The thing I enjoy the most about being a founder is the flexibility that comes with being a founder. Um, you have the flexibility to work when and wherever you want, um, and you also have the flexibility to grow and start a company that looks and feels the way you want it to. Um, because it's all on you to figure out what your company is gonna look like, whether that's operationally or culturally. Uh, you know, you have flexibility to kind of create the world you wish to work in, so to speak. And you can take all the things you loved from previous roles and incorporate them or all the things you hated and kind of leave that out. Right. So you really have full control over what your day-to-Day looks like for the most part, which I really enjoy.",
          },
          {
            vimeoId: "937999533",
            advizerId: "27e2b536-49c3-4c03-821e-cbe15504cec7",
            transcript:
              "What do you enjoy most about being in your current role? So, I love meeting new people. I love building relationships in the different industries, and I absolutely love learning about new companies and not just new companies, but new industries, new trends. Um, it's constantly new for me and I love that. I love that every day I get to read about a new business or a new industry or, um, you know, a new dilemma that's going on within a sector. Um, I think that just is very fulfilling for my intellectual curiosity. And then I also love building the relationships and, um, getting to know more people. And my job does require travel and I personally like to travel, so all of my travels within the us. Um, and I do like going to the major cities. You know, I go to New York a lot, I go to LA a lot and Chicago. Um, and I like that. I like being a, you know, being on the road. And then once we invest in a business, then I get to be a part of watching it grow and change and evolve. And I get to see all the good that we bring to these companies, whether it's, you know, capital to build out inventory or capital to expand into a new facility or manufacturing site, or to help them with marketing to grow internationally, or maybe it's new product development. I get to see a business from where we acquired it and then maybe, you know, 5, 7, 10 years down the line, you get to see how much the company has evolved and grown. And, um, we've been a part of that. And I get a lot of, um, I get a lot of sense of pride and satisfaction with being part of that support on the company's now the next journey.",
          },
        ],
        top3Transcripts: [6, 2, 14],
        reasoning:
          "Based on Emily's CareerLeader report, she values Enterprise Control, Theory Development/Research, and Coaching/Mentoring. She also has strengths in analysis, strategic decision-making, and interpersonal effectiveness. Her ideal career matches include Strategic Planning, Entrepreneurship, and General Management. She also appreciates variety and flexibility. Given this information, I looked for transcripts highlighting roles that offer these characteristics. Transcript 6 (consulting) emphasizes people-oriented work, flexibility, and continuous learning. Transcript 2 (founder) highlights the flexibility and control in shaping a company's culture and operations. Transcript 14 describes the excitement of meeting new people, building relationships across different industries, continuous learning about new companies and industries, and the fulfillment of helping companies grow which aligns with Emily's profile.",
      },
      {
        questionNumber: 6,
        videoDataForTop3Transcripts: [
          {
            vimeoId: "878803198",
            advizerId: "061c1099-ff72-42b1-8337-81446490f56f",
            transcript:
              "What is your biggest challenge in your current role? I would say my biggest challenge is around time management on a day-to-day basis, of course, but there's also no real nine to five punch in punch out with a startup organization. So I typically work seven days a week, even if it's just for an hour or two on the weekends. And I would say part of that is the demands of the startup leadership position, but also it's my own desire to just always be making progress, always feeling like there's something I could be doing that day, not wasting any time. But I do realize, and I will tell anyone that's in a similar position, you have got to set boundaries and find a way to enjoy the work-life balance. But of course, that's easier said than done because fundraising never pauses and startups never sleep. So it's all about kind of finding that happy medium, perfect balance, but understanding that as a founder or really anyone working for a startup company or organization, there's a whole lot of work to do and you're gonna feel pressure to always be making progress.",
          },
          {
            vimeoId: "990339554",
            advizerId: "c44516be-4429-4491-9975-d4b83d3ebbb4",
            transcript:
              "What is your biggest challenge in your current role? I'd say it's prioritization of, um, responsibilities. I think, you know, there's many things you have to work and focus on, starting with one, the client work. So you have to know, you know, what needs to be done now, what needs to be done soon, what can be done later. And then, you know, in my role as a senior consultant, I also have to focus on the business proposals of my actual company, um, Deloitte. So that's something we have to prioritize too, you know, making sure we spend the extra hours and business related pursuits. And then there's also career development and growth. You know, there's so many certifications and things to learn. I know with AI right now, everyone's learning about ai, so there's new trends in technology and in the financial world to learn. So essentially there's a lot of things happening at the same time. And it's just, I think the biggest challenge for me is just making sure I can prioritize the right things while also having my work-life balance afterwards.",
          },
          {
            vimeoId: "900227606",
            advizerId: "5b060460-7ca6-4247-b4e1-aebb6e4af4b6",
            transcript:
              "What is your biggest challenge in your current role? Yeah, I would say the biggest challenge, uh, that I've faced has really been finding balance. And so, you know, as you enter the workforce, um, you wanna work very, very hard, but you also wanna find a good balance to be able to take care of yourself. And in a role like consulting where there is a lot of travel, uh, the hours can be varied, vari varied based on the project. You know, it, it, it's, it's difficult to find that professional and personal balance. Um, and so that's one thing that I, but that can be unique to, to any job. I think another challenge that, uh, I'm facing in my current role is I'm getting ready to, uh, kind of make the jump from being a junior team member to more of kind of a mid-management, um, and, and, and more of a people manager, um, as I complete my, uh, MBA and, and everything like that. So, uh, you know, navigating that, that jump and that kind of change in responsibilities is certainly, uh, something I'm facing currently in my role.",
          },
        ],
        top3Transcripts: [1, 4, 6],
        reasoning:
          "Based on Emily's CareerLeader report indicating interests in areas like Strategic Planning, General Management, and Entrepreneurship, along with motivators like Power/Influence and a desire for Autonomy, I looked for transcripts that addressed challenges related to those career paths and motivators. Transcript 1 discusses time management and work-life balance, especially relevant in startup environments, fitting her high scores in strategic thinking and entrepreneurship. Transcript 4 focuses on prioritization, which is key in management roles, as well as career development, something important to someone interested in continuous learning and strategic roles. Transcript 6 explicitly mentions finding balance and transitioning into a management role, aligning with her MBA completion and leadership skills. These transcripts address challenges in areas she's likely to pursue and offer insights into navigating those challenges.",
      },
      {
        questionNumber: 7,
        videoDataForTop3Transcripts: [
          {
            vimeoId: "900227637",
            advizerId: "5b060460-7ca6-4247-b4e1-aebb6e4af4b6",
            transcript:
              "What do you enjoy most about being in your industry? Yeah, so, uh, one of the things I enjoy most is the ability to have real, tangible impact on some of the most recognizable companies in the world. Um, so I've had the privilege early on in my career of working with, uh, a number of companies that are on the Fortune 500, some of them, you know, towards the top of that list. And they are truly some of the most well-known brands and companies, uh, globally. And so I enjoy, uh, you know, having the opportunity to interact with, with those client teams and work at those clients. And then the other piece to this is, you know, I I, I love the ability to have continuous change. Um, as I mentioned, you know, uh, consulting is a place where you can come and, and experience a lot of different industries, uh, in a short time span. And so there's a lot of learning involved, there's a lot of change involved. And, and I enjoy that, uh, because it kind of keeps the, the role and the job fresh and new, um, you know, at the start of each project.",
          },
          {
            vimeoId: "833832203",
            advizerId: "6d0f799c-5779-4d21-8a99-714815d6adca",
            transcript:
              "What do you enjoy most about being in the advertising industry? Um, what I love about advertising and specifically ad tech, right, advertising technology is it's always evolving. Um, every year there's a million new, new things to learn and it even, you know, changes by month. Um, there's always new partners in the space, new competitors, and that keeps things interesting. Um, there, there's always something to learn. There's always new trends. Um, I happen to be in the space of AI within ad tech and that's super interesting to me, right? And AI, of course, is around us all over, uh, you know, and outside of ad tech as well. But I, I feel very fortunate to know a little bit about, uh, this growing space in adtech, and I'm happy to share that with, uh, my prospects as well. Um, so I, I'd say that is the number one thing, you know, and, and kind of a, a side to that is now that I've been in Ad Tech for, uh, nine plus years, you know, I've built relationships that have, believe it or not, lasted all nine years. And, and, you know, at every company I'm meeting people that I enjoy working with, I meet clients that I've done business with for the past 5, 6, 7 years. Um, so those relationships are important, um, and it's nice to see them growing along with me in the industry.",
          },
          {
            vimeoId: "801745777",
            advizerId: "b2959226-aca2-48e8-ac7e-673a3d4634c5",
            transcript:
              "What do you enjoy most about being in your industry? So the industry I'm in now is you can Define it a couple of ways I work in the space industry the Aerospace industry technology if you will but it has a national security into it National Security means a lot of things in my context. It really means. National Defense or you know defense security, you know, if you're gonna I guess wrap it like that but the most interesting part about that the reason why it's so awesome is because it's a dynamic environment. It is dependent on geopolitics. It's dependent on food scarcity on energy technological developments finances. So when I wake up in the morning every day, it's I don't have a linear. I don't know what's gonna happen tomorrow. Well, it's also one of the things I liked about being in the military was every single day. It came to work there was potentially something new that we had to go solve. And so every day I wake up in the morning, you know, there's a new there's a new you know acquisition. There's a new company like funded there's a new program that's coming online in the government or something happened in Russia or Singapore or South America and all those things impact what I'm gonna do that day it impact the The opportunities and the risks that exist. And so that's what that's probably the most fun part about the job is that it's Dynamic and it's you know, keep you on your toes. Then you have to really read a lot and before thinking to be able to succeed and so it's fun. It's really fun in that respect.",
          },
        ],
        top3Transcripts: [6, 3, 13],
        reasoning:
          "I reviewed each transcript, prioritizing those that aligned with the student's interests, motivators, skills, and career matches from the CareerLeader® report. I focused on transcripts discussing topics such as strategic decision-making, leadership, entrepreneurship, and working with diverse teams. Transcript 6 emphasizes having a tangible impact on recognizable companies and continuous change through consulting, aligning with the student's interest in 'Enterprise Control' and 'Strategic Planning'. Transcript 3 highlights the fast-evolving nature of advertising technology, appealing to the student's 'Creative Production' interest and 'Strategic Thinking' skill. Transcript 13 focuses on the dynamic environment of the space industry and national security, fitting with the student's enjoyment of dynamic environments and new challenges.",
      },
      {
        questionNumber: 8,
        videoDataForTop3Transcripts: [
          {
            vimeoId: "900227670",
            advizerId: "5b060460-7ca6-4247-b4e1-aebb6e4af4b6",
            transcript:
              "How would you describe people who typically thrive in this industry? I would say people that typically thrive, um, are those that enjoy working with other people and get energy, um, from, from, you know, being around, uh, other people and, and, and having to develop relationships, uh, pretty consistently. Generally what I see is there is quite a few type A people in consulting. Um, now that's not to say that those kind of type B people and people who do, who do not enjoy, uh, the, the people aspect of it are don't thrive and, and, and can't find their, their niche within consulting. I'm, I'm definitely speaking broadly here. Um, but in general, you know, a lot of type A people, most people are, are kind of people oriented, um, very goal oriented folks, uh, and, and people that are very comfortable with interacting with and managing different people and different personalities. Um, most people have a, a, a high degree of flexibility too, uh, and being able to, to be very flexible. And the other thing is that, uh, most of the people I find who thrive, uh, who I've worked with and who I've en enjoyed learning from are those who, you know, they have a perspective, they bring a perspective to the table, they're convicted in their beliefs. Um, and, but, but they're, you know, open to, to, to, to debate and, and to change when needed. And I think that that's very important when you, when you join a, a consulting role.",
          },
          {
            vimeoId: "937999570",
            advizerId: "27e2b536-49c3-4c03-821e-cbe15504cec7",
            transcript:
              "How would you describe people who typically survive in this industry? I think people in this industry are, uh, very passionate about what they do. I think they're, uh, very hardworking. Um, I think they're very analytical. I think they, um, thrive in environments where there might, there may be a little bit more ambiguity within the role, and you kind of have to show up and be ready to wear a lot of hats, be ready to, you know, be doing different things each day. You know, one day you may be analyzing a company the next day you may be at a trade show doing, uh, industry research. The next day you may be, um, talking to investment bankers and building relationships. So you have to be able to sort of change your hat and change what you're doing at a, you know, in a very quickly paced environment. Um, so I, I also think intellectual curiosity, I just think that's so important to wanna learn more about businesses, to wanna learn more about industries is absolutely critical. And, and that analytic analytical mindset that I alluded to earlier. Um, you know, being comfortable with numbers, being comfortable with people, and being comfortable thinking about the bigger picture, whether it's on, you know, a macro industry trend or maybe it's just strategic thinking within a company. It's having that broad, broad approach to the business, um, I think is extremely helpful. I.",
          },
          {
            vimeoId: "836299990",
            advizerId: "08b417e7-e236-4ad8-be68-bc16e5c1d187",
            transcript:
              "Okay. How would you describe people who typically thrive in this industry? Well, if we're talking about business ownership, I would say, um, just knowing that, um, you're never going to knock it outta the park and, um, do it perfectly. It's just not realistic. So I think staying humble, but getting up and dusting that sh that, uh, dust off your shoulder. Uh, you're constantly faced with setbacks and adversity. So if you have the ability to have thick skin and to just keep propelling forward and stay focused on your mission and your vision, that's incredibly important. But also knowing when you need to recalibrate and pivot and make a change sometimes, um, I truly believe that we have these objections and these, um, things that happen in life, and you have to sort of know when do I push that door and when do I say, okay, that door needs to be closed and I need to move on. Um, and that's hard, that's like a lifelong skill. But I think as far as, you know, who's gonna thrive in, in this industry of, of business ownership, it's gonna be people who just keep putting one foot in front of the other. Um, that's probably the most generic answer, but I would say secondary to that is, um, networking. Cuz networking is really your net worth. And that is with everything. You just never know who you're gonna meet that is able to help you. But more importantly, who can, who can you help? And I really think the more that you help others, you will get that back tenfold. But really staying, um, true to networking and meeting people and never knowing, you know, how those bridges may connect or how those paths will cross, um, in the future, I think is, is probably the second most important thing I would say about business ownership.",
          },
        ],
        top3Transcripts: [6, 14, 10],
        reasoning:
          "Based on Emily's CareerLeader report and 360 feedback, I looked for transcripts emphasizing traits like a strong work ethic, strategic thinking, relationship building, and adaptability. I focused on roles where influencing others, leadership, and communication are key.  The 'watch-outs' also guided my selection, prioritizing transcripts that addressed the need for sensitivity to organizational culture and networking. Transcript 6 stood out due to its emphasis on people-oriented, goal-oriented, and flexible individuals comfortable with managing different personalities, aligning with Emily's interpersonal skills and leadership potential in consulting. Transcript 14 highlights analytical skills, intellectual curiosity, adaptability in ambiguous environments, and comfort with numbers, crucial for strategic roles and consulting. Lastly, transcript 10 emphasizes resilience, networking, and continuous learning from setbacks, matching Emily's need to develop her professional network and persevere in business ownership or general management.",
      },
      {
        questionNumber: 9,
        videoDataForTop3Transcripts: [
          {
            vimeoId: "900227701",
            advizerId: "5b060460-7ca6-4247-b4e1-aebb6e4af4b6",
            transcript:
              "What have you learned about this role that you wish someone would've told you before you entered the industry? Uh, you know, I think one of the things that I've learned over time is that the experience that you have, uh, within a consulting firm and within a, the consulting industry is, is dictated generally by the people you surround yourself with and less on the project. And so I have found that early on, uh, that working with good people has made the role significantly more enjoyable than the, the, the topic or the nature of the project that I'm working on. I think if you, uh, come into consulting, you know, my general advice would be seek out good people early on who seem to have your, your best interests at heart, who are, who are good mentors, who are willing to invest their time into your development. And that can have an e an exponential, uh, impact on, on anyone's consulting experience.",
          },
          {
            vimeoId: "881616165",
            advizerId: "4d5d29ac-4186-4b92-ab92-c8609653d063",
            transcript:
              "What have you learned about this role that you wish someone would've told you before you entered this industry? What I've learned about being a business owner is that it's not that hard to be a business owner, but it is hard, um, to have a successful business. So, let me explain. I think I would've started my own business much sooner if I realized that I had the full capacity to be able to do it. I only started my business when I was in an MBA program at UCLA and was surrounded by classmates and professors and resources and exec, executive coach, and all sorts of people who could help me out and who were happy to help. But if you have expertise in a specific area where you wanna start your business, and you have people in your life who you can reach out to and ask for help, or if you are the type of person who's comfortable in making new connections and new relationships with people you can depend on and ask for help, who want to support you, that's really all you need to get started. Um, and running a business is not rocket science. Um, you're gonna have to figure out accounting and you're gonna have to figure out legal stuff, but all of that information is available. Um, running a profitable business is the hard part. I think there's a statistic that most small businesses fail within the first two years. Um, and I've been around for almost three years now, so I feel like I've hit, um, a milestone. Um, some of the things that have kept me in business, um, again, not being afraid to ask for help. Um, not being afraid to take risks. Um, having a understanding of what else is happening in my industry, in my sector, so who I'm competing with, which sometimes aren't direct competitors, um, but instead, um, other companies or individuals that are meeting the need of my client in a different way than I would need it. Um, also knowing, um, just having a clear set of goals and knowing what I want to have achieved. Like, I think I think about, maybe this is morbid, but I think about what I want in my obituary. I think about, you know, I, if, if someone is writing my obituary and talking about who I am as a person, and also mentioning the work that I did with my company, I want that to be something exciting and something memorable and something really, really cool. I don't wanna, you know, like you read obituaries where you're like, so-and-so worked at a restaurant. I was a waitress. I'll use that example. She was a waitress for 30 years. Like, oh my God. Um, that's not what I want for my life. That's not how I wanna be remembered. Um, I want to be, um, I, I want to be remembered as someone who made a, made a difference. And I think that if someone had told me sooner that that was possible, I would've dived into this work sooner.",
          },
          {
            vimeoId: "990340141",
            advizerId: "c44516be-4429-4491-9975-d4b83d3ebbb4",
            transcript:
              "What have you learned about this role that you wish someone would've told you before you entered the industry? Um, up my presentation skills. I think that's, I, I wish I knew, I wish I learned more about that coming in. So like, PowerPoint in the consulting industry is the name of the game. You have to know, you know, you could present, you could create a really cool deliverable, but you have to know how to present it in a really concise way to a client. And that, that concise way that could differ based on who the client is. Some people like, you know, heavy information on a slide. Some people like very clear graphics without much text, so they could look at it for 20 seconds and then understand the point. So I think it's really just PowerPoint presentation, storytelling through PowerPoint is something I wish, you know, I learned more beforehand and I wish someone had told me just because it's maybe one of the most important skills in consulting because you just, you could do the work, but you have to narrate and present the work too. And that's a very difficult thing, but definitely a good skill to have.",
          },
        ],
        top3Transcripts: [6, 16, 4],
        reasoning:
          "Based on Emily's CareerLeader results, she is looking at roles in Strategic Planning, Entrepreneurship, and General Management, and has strengths in Analysis and Strategic Decision-Making, Bringing Management Structure, Interpersonal Effectiveness, and Power and Influence. I looked for transcripts that provided advice on these areas and could help guide her as she enters her career. Transcript 6 provides the best advice that the experience is dictated by the people you surround yourself with, and she should seek mentors and people who will invest in her development. Transcript 16 helps prepare her for the business owner side of entrepreneurship and building a business, as well as the risks involved. Transcript 4 recommends that she improves her presentation skills, which is a skill that will help in consulting.",
      },
      {
        questionNumber: 10,
        videoDataForTop3Transcripts: [
          {
            vimeoId: "900227727",
            advizerId: "5b060460-7ca6-4247-b4e1-aebb6e4af4b6",
            transcript:
              "What entry-level positions are there in this field that an undergraduate college student might consider? Great question. Uh, fortunately, I I, I would hope for most, most undergrads who might be listening to this, um, every consulting firm that I can think of has a analyst or business analyst type entry-level role. Um, that is, that it's available for undergrad recruiting. Um, consulting is a industry that the reco recruiting process is very structured, uh, generally that there are a number of target schools that, that major consultancies recruit from. Um, that recruiting will begin most times it begins in September of the fall. So if you're a, um, rising senior, my, my advice to you and you're interested in breaking into consulting, my advice to you would be to, to start that preparation in your junior summer. Um, so you are ready to hit the ground running, uh, during, during recruiting. That picks up early in the fall. Uh, if you're, if, if you're from a university that may not, uh, have a lot of consultancies coming to, to campus, I would encourage you to, to reach out, uh, to, to find the kind of recruiting email contact, uh, for the consultancy that you're interested in and, and use that, uh, be proactive about using that as a, as an inroad. Um, but in general, you know, uh, there are a number of analyst roles that are hired. Um, consulting is kind of one of the major industries that hires, uh, undergraduate, you know, or recent grads, um, into full-time roles. And so, uh, you know, there's a, a number of options, uh, for people to consider.",
          },
          {
            vimeoId: "990340445",
            advizerId: "c44516be-4429-4491-9975-d4b83d3ebbb4",
            transcript:
              "What entry level positions are there in this field that an undergraduate college student might consider? So, something I like to tell people is always consider like every, uh, consulting company, right? So like, currently I'm in a big four, but you know, if that's not, if jobs there are not available, there are smaller consulting firms that provide valuable experience. So when I joined the company, I mentioned before M two strategy, I joined them in an entry level position. So I learned about consulting there and like financial consulting, risk management from the ground up. And that's something I, I like to tell people to consider and join because then you get to learn about it in a smaller, less intense setting. And then that's, that kind of helps enable that growth there and lets you take all those skills to the next level. So definitely consider those smaller consulting companies.",
          },
          {
            vimeoId: "937999596",
            advizerId: "27e2b536-49c3-4c03-821e-cbe15504cec7",
            transcript:
              "What intro level position are there in this field that an undergrad college student may consider? I think one of the biggest feeders into investment, or sorry, excuse me, into private equity is investment banking. So I would say the majority of the people go into an analyst program at an investment bank and then get two years of experience and transfer into private equity. So I think that's an absolute great feeder, but that's the obvious feeder. I think, you know, within my role in business development, anything that you're doing where you're analyzing businesses, so a consulting firm or even where you're doing, um, interpersonal skills and networking, maybe it's a communications role, you know, but if it's tied to finance, so maybe you're a communications, uh, in a communications role, but you're focused on finance companies, that could be very interesting. I know someone who used to be a journalist, she was a journalist focused on finance companies, particularly on investment banking and private equity, and she ended up going into a slightly similar role from a journalism perspective. So any role where you have, um, you know, strategy or consulting or finance would be a great entry level role for this.",
          },
        ],
        top3Transcripts: [6, 4, 14],
        reasoning:
          "Based on the CareerLeader report, Emily has high interests in Enterprise Control and Theory Development/Research. Her career matches include Strategic Planning, Entrepreneurship, General Management, and Management Consulting. The transcripts that provide the most helpful information should discuss those fields. \n\nTranscript 6 directly addresses consulting as a career path for undergrads, discusses the recruiting process, and provides advice on how to prepare. This is highly relevant given Emily's interest in strategic planning, general management, and management consulting.\n\nTranscript 4 mentions smaller consulting firms as a good starting point to learn financial consulting and risk management from the ground up, which can enable growth and taking skills to the next level. \n\nTranscript 14 discusses investment banking as a feeder into private equity. It then mentions that a consulting firm or communications role focused on finance companies would be a great entry level role. This is more relevant than other transcripts that do not focus on consulting and are in very different fields.",
      },
      {
        questionNumber: 11,
        videoDataForTop3Transcripts: [
          {
            vimeoId: "878803382",
            advizerId: "061c1099-ff72-42b1-8337-81446490f56f",
            transcript:
              "What is one lesson that you have learned that has proven significant in your career? A big lesson I have learned through this job, and I will pass it on to anyone who is willing to listen to me, is that communication and networking is really just a cheat code to succeeding in this career, or really any career for that matter. So again, circling back to what I said before, finding that confidence or the willingness to walk up to a random person in the room, it could really take you far. You just never know where that connection may lead you, how it may help you within your job or, you know, maybe you circle back a few years later and you're working together on another project, but just that communication and networking, any chance you get is really going to help you go super far with this position and in the rest of your career as well.",
          },
          {
            vimeoId: "900227753",
            advizerId: "5b060460-7ca6-4247-b4e1-aebb6e4af4b6",
            transcript:
              "What is one lesson that you have learned that has proven significant in your career? I'm actually gonna break this into two. Uh, there's two things that I've learned that I think, uh, are, have been invaluable pieces of advice that I remind myself of every day. The first is, uh, you know, don't be afraid to admit what you don't know, but work like heck to figure out whatever it is. Um, you know, there, there's something to be said that was a piece of advice that was given to me, um, very early on. Uh, particularly as you get into more client facing goals and you face tough questions, um, you know, there's gonna be things that, that come up that you don't know. And, and, and being willing and, and, and having that conviction to say, well, I don't know, but I will figure it out. Um, you know, people respect that and, and, and that that shows that you have a can-do attitude. Um, but it also, you know, it shows that you are willing to admit when you have a shortcoming. The other thing is, I would tell people is, uh, the other lesson that's really stuck out to me is to really have confidence that you belong somewhere. Um, so consulting in particular is a industry where you can come in and from day one you can be in a room of people who are client team, who everybody there is 20 years your senior, and they've been working in a specific industry for 20 years. But that doesn't mean that you as a recent undergraduate hire, uh, that's part of the consulting team, don't have some sort of value add. And so, you know, have confidence that, you know, you belong in the setting that you're in. Uh, you're there for a reason and you know, just kind of figuring out how you can add value to any given situation with that confidence that you belong there will take, will take anybody, uh, a long way.",
          },
          {
            vimeoId: "801745879",
            advizerId: "b2959226-aca2-48e8-ac7e-673a3d4634c5",
            transcript:
              "What is one lesson that you have learned that has proven significant in your career? mentor it is hands down the most important thing. If you don't have a mentor you're walking through the forest and you can't see the power. Chances are you'll end up where you're going where you want to go and you're probably learn a lot along that Journey, but it's much faster if you have a mentor and so it's tough to go find when you don't go to Target and find a mentor on the shelf and I've seen programs where they try to pair mentors and but it's like that's like forced marriage of course dating doesn't really work. the best way to get out there is You know write blogs show up at events email people. Do a little bit of Sweat Equity. You're gonna have to whatever you put in you're gonna get out and then somebody's gonna bring you under their way. because they're gonna be enthusiastic because you're enthusiastic and a lot of people they want to talk like me, but also want the pass along to the Next Generation so that they're gonna be successful and so If you want to jump start your career somebody that's gonna teach you the ropes. I would definitely try to find that person that's been an industry. Whatever it is. Whatever the entry is that's been doing it for 10 or 20 or 30 years 30 years might be too far but maybe the 20 year mark where you're talking to people that are 40 45 years old maybe 50 that are looking at their kind of Horizon and but they want to pass off everything they know about the industry to somebody that's going to be in the following their footsteps that is hands down the most important thing because that's how you're gonna open up a bunch of doors.",
          },
        ],
        top3Transcripts: [1, 6, 13],
        reasoning:
          "Based on Emily's CareerLeader report, she has high scores in Interpersonal Effectiveness, strategic decision making, and an interest in coaching/mentoring. Her watch-outs include developing/maintaining a professional network and being sensitive to organizational feel. \n\nTranscript 1 emphasizes communication and networking as a 'cheat code' to success, directly addressing Emily's watch-out and aligning with her interpersonal skills.\n\nTranscript 6 discusses the importance of admitting what you don't know and working to figure it out, which aligns with continuous learning and development. It also highlights having confidence and adding value in client-facing situations, relevant to strategic planning and management consulting career matches.\n\nTranscript 13 highlights the importance of having a mentor to guide career growth. This aligns well with Emily's interest in coaching/mentoring and addresses her need to develop a professional network.",
      },
      {
        questionNumber: 12,
        videoDataForTop3Transcripts: [
          {
            vimeoId: "878803424",
            advizerId: "061c1099-ff72-42b1-8337-81446490f56f",
            transcript:
              "What did you do in your undergrad to set you up for success in your career? I chased every single opportunity that presented itself, even if it took away from my social life, even if it pushed me outta my comfort zone. And I also sought out a mentor, one of my professors, who he really recognized my passion and my drive and used that to help me hone in on my skillset, which made a number of game changing introductions for me as well. And he really helped guide me in what was next after graduation, which can be a really scary time. So to have someone who's been through it, who has succeeded in that industry and has a lot of those connections that are critical can really help so much. It can help your confidence, but it can also help your networking and set you up for success. I really believe that connections are everything, and I encourage everyone to always remember that and always seek out a mentor if you can.",
          },
          {
            vimeoId: "900227776",
            advizerId: "5b060460-7ca6-4247-b4e1-aebb6e4af4b6",
            transcript:
              "What did you do in undergrad to set you up for success in your career? I would say that one of the things that was extraordinarily beneficial to me, um, was being able to get some level of work experience on my resume prior to applying for a full-time role. Um, so if there are any college juniors out there or college juniors or below who are thinking about a, a career in consulting, you know, I would definitely encourage you to do a summer internship. Uh, it doesn't have to be a consulting related or in the consulting field, but having some level of work experience and something to speak to, uh, as you prepare for a full-time position is something that I did and it was extremely beneficial. And the other thing that I would tell people is, um, you know, consulting, recruiting, as I mentioned earlier, it starts very early. And so, uh, if this is something that, you know, you want to be a, you want, you want to be in, you wanna pursue, um, start early, you know, start your networking early figure out, get your list of school alums who work at specific firms, reach out to them. Um, you know, whether it's, whether you can get an email, uh, for them or reach out to them on LinkedIn. I, I find that most people are willing to take 15 to 20 minutes to chat. And so starting that career exploration early, um, having a game plan and, and being ready to hit the ground running before recruiting starts is, is, uh, is invaluable and something I would encourage everybody to do, um, as they're thinking about potentially pursuing a career, uh, in the consulting field.",
          },
          {
            vimeoId: "866168874",
            advizerId: "90d873be-e874-4ed7-902e-d52cab20a797",
            transcript:
              "What did you do in undergrad to set you up for success in your career? Yeah, that's a great question. So I, I'm thinking of three things right now. One is traveling and becoming worldly. You know, I studied abroad, then I entered abroad, and I got to travel to a lot of amazing places. I'm, I feel so fortunate for those opportunities, but that also made me a very relatable person. So I'd say number two is my ability to network, and I think I was a pretty social person in college, but it also combined, you know, the worldly view, the, all the experiences, my ability to connect with, uh, people. I think that really helped me move up the ladder faster in my corporate job and also have this, you know, large network that I'm connected with for my company now. So I think those are two big pieces of it. And the third is that I learned how to work hard. My, uh, my last year I actually, uh, I took the c p a exams and, you know, I kind of shifted my lifestyle where being of college, I was partying a lot at that point, you know, I was really focused and I was studying, you know, 4, 6, 8 hours a day, you know, five days a week towards these tests. That prepared me for working these long hours in my consulting job. Um, it wasn't as much of a shock to the system as the people who skated through college and then got to the real world and were like, what is this? What do you mean we have to focus for eight hours straight or 10 hours straight? This is crazy. So, you know, I think that a lot of us wanna always take the easier path where any challenges you bring into your life, those will actually pay off in dividends down the road, because they will call your mind to prepare you for whatever's next. Then the last piece is also just like building healthy habits. I started exercising a lot in college, started running, I did, you know, my first half marathon. Then I, you know, as I've worked over the years, I still do marathons and triathlons every year, and for me, that keeps me sharp. That keeps me in the head space to keep pushing and keep going in business. I think that it also gives me the confidence, like, if I can push through a marathon, finishing this, you know, document isn't that bad. You know, I, I've, I've dealt with work worse than this. So build healthy habits now that will support you when the going gets tough in the workplace.",
          },
        ],
        top3Transcripts: [1, 6, 9],
        reasoning:
          "Based on the CareerLeader report, Emily is interested in Enterprise Control, Theory Development/Research, and Coaching/Mentoring. She has strong skills in Analysis and Strategic Decision-Making, Interpersonal Effectiveness, and Power and Influence. Her Career Matches include Strategic Planning, Entrepreneurship, and General Management. Her CultureMatch leans towards Collaboration, Extraversion, Innovation, and Precision. Watch-Outs include being sensitive to organizational feel and developing a professional network. \n\nTranscript 1 emphasizes the importance of networking and mentorship, which aligns with Emily's need to develop her professional network. Transcript 6 focuses on gaining work experience through internships and early networking, particularly in consulting. This advice is helpful given Emily's career matches include Management Consulting. Transcript 9 covers traveling and becoming worldly, networking and building connections, learning how to work hard, and building healthy habits. Each of these items could benefit Emily as she moves forward.",
      },
    ],
    reasoning:
      "To identify the best-matching advizers, I analyzed Emily's CareerLeader® report, focusing on her interests, motivators, skills, career matches, CultureMatch™, and watch-outs. I then compared these attributes to the bios, majors, traits, industries, and roles of each advizer, assigning higher relevance to advizers with stronger alignments across multiple categories. I paid particular attention to individuals in Strategic Planning, Entrepreneurship, General Management, Advertising Account Management, Sales Management, Non-Profit Management, Human Resource Management, Research and Development Management, Training and Organizational Development, Management of New Product Development, Business Development and Sales, Management Consulting, Marketing and Marketing Management, and Retail Management roles.",
    recommendedAdvizers: [
      {
        id: "a1fbf7bc-91af-4f2b-be90-803ad7e0f2d4",
        name: "Michael Cohen",
        jobTitle: "Senior Consultant",
        reasoning:
          "Michael's role as a Senior Consultant at West Monroe, with experience in M&A transaction execution and technology-oriented strategy, aligns well with Emily's career matches in Management Consulting and her skills in Analysis and Strategic Decision-Making.",
      },
      {
        id: "061c1099-ff72-42b1-8337-81446490f56f",
        name: "Kyle Crognale",
        jobTitle: "Founder",
        reasoning:
          "Kyle's role as Founder of Parichute and experience in client recruitment, public relations, and project management connects with Emily's Entrepreneurship career match and her Interpersonal Effectiveness skills.",
      },
      {
        id: "4bc9ef66-1b6b-4c25-a1dc-f408d2afd3eb",
        name: "Sterling Champion",
        jobTitle: "Creator & Founder",
        reasoning:
          "Sterling's role as Creator & Founder of The SHE Mark, along with experience in operations, event production, logistics, and branding, aligns with Emily's Entrepreneurship career match and her interest in Creative Production.",
      },
      {
        id: "6d0f799c-5779-4d21-8a99-714815d6adca",
        name: "Andy Green",
        jobTitle: "Sr. Director of Sales",
        reasoning:
          "Andy's role as Sr. Director of Sales in the advertising world and Adtech, combined with Emily's interest in Influencing Others and career matches in Advertising Account Management and Sales Management, makes them a strong match.",
      },
      {
        id: "c44516be-4429-4491-9975-d4b83d3ebbb4",
        name: "Fahim Badal",
        jobTitle: "Senior Consultant",
        reasoning:
          "Fahim's role as a Senior Consultant at Deloitte aligns with Emily's Career Matches such as General Management, Management Consulting and skills such as Analysis and Strategic Decision-Making. ",
      },
      {
        id: "a30d12e3-b19c-4165-9be9-2d7567c8d6db",
        name: "Shirley Chen",
        jobTitle: "CEO and Co-Founder",
        reasoning:
          "Shirley's role as CEO and Co-Founder of Preciser, specializing in AI-driven analytics and product development in sports data analysis and technology, aligns with Emily's Entrepreneurship career match and skills in Analysis and Strategic Decision-Making.",
      },
      {
        id: "5b060460-7ca6-4247-b4e1-aebb6e4af4b6",
        name: "James McGhee",
        jobTitle: "Senior Consultant",
        reasoning:
          "James' role as a Senior Consultant at Deloitte Consulting, specializing in strategy and operations consulting across industries like retail, healthcare, and financial services aligns with Emily's Career Matches such as General Management, Management Consulting and skills such as Analysis and Strategic Decision-Making. ",
      },
      {
        id: "e8e6b259-d499-45e9-a86e-ea4418ce681f",
        name: "Alex King",
        jobTitle: "Senior Product Manager",
        reasoning:
          "Alex's role as a Senior Product Manager at Amazon, with experience in product management, marketing, and analytics for SaaS and e-commerce, is relevant to Emily's skills in Analysis and Strategic Decision-Making and her career match in Marketing and Marketing Management.",
      },
      {
        id: "653db63f-b5e6-456b-bb2e-2c946b4dd65f",
        name: "Dana Old",
        jobTitle: "Executive Coach and Trainer",
        reasoning:
          "Dana's role as an Executive Coach and Trainer, specializing in team leadership, brand management, and coaching aligns with Emily's Skills such as Interpersonal Effectiveness and Power and Influence and Career Matches such as Training and Organizational Development.",
      },
      {
        id: "90d873be-e874-4ed7-902e-d52cab20a797",
        name: "Brian Dubow",
        jobTitle: "CEO and Founder",
        reasoning:
          "Brian's role as CEO and Founder of Hit of Happiness, focusing on wellness/happiness coaching and consulting, connects with Emily's interest in Coaching/Mentoring and skills in Interpersonal Effectiveness and Power and Influence.",
      },
      {
        id: "08b417e7-e236-4ad8-be68-bc16e5c1d187",
        name: "Jessica Thumati",
        jobTitle: "CEO",
        reasoning:
          "Jessica's role as CEO with experience in entrepreneurship, financial advisement, and strategy, combined with her industries of Consulting & Related Professional Services align well with Emily's Career Matches such as General Management and skills such as Analysis and Strategic Decision-Making.",
      },
      {
        id: "dbd2ee7b-45b4-40ca-aaa3-7cb7eb2b4d62",
        name: "Cheddy Matthews",
        jobTitle: "Executive Leadership Coach",
        reasoning:
          "Cheddy's role as Executive Leadership Coach, transforming leaders, and experience with diverse backgrounds, aligns with Emily's career matches such as Training and Organizational Development and her Skills such as Interpersonal Effectiveness.",
      },
      {
        id: "08a0caee-01f8-4a5d-8d58-aaf12b097605",
        name: "Linda Yeung",
        jobTitle: "Executive Director, Leadership Development Program",
        reasoning:
          "Linda's role as Executive Director of a Leadership Development Program in government connects to Emily's skills in Interpersonal Effectiveness and Power and Influence and Career Matches such as Training and Organizational Development.",
      },
      {
        id: "b2959226-aca2-48e8-ac7e-673a3d4634c5",
        name: "Mike Sims",
        jobTitle: "Advisor",
        reasoning:
          "Mike's role as Advisor for an Independent Consulting Company aligns with Emily's Career Matches such as General Management and skills such as Analysis and Strategic Decision-Making. and her excellent work ethic mentioned in the 360 feedback.",
      },
      {
        id: "27e2b536-49c3-4c03-821e-cbe15504cec7",
        name: "Katharine (Katie) Melzer",
        jobTitle: "Managing Director/Head of Business Development",
        reasoning:
          "Katharine's role as Managing Director/Head of Business Development connects to Emily's excellent work ethic, strong leadership skills, and career matches such as Strategic Planning and General Management, as well as Business Development.",
      },
      {
        id: "c999e30f-aed3-41ff-8b6b-c3b00b1bcd3b",
        name: "Emily McSherry",
        jobTitle: "Manager, Learning and Development",
        reasoning:
          "Emily's role as a Manager, Learning and Development in the consulting and recruitment industries aligns well with Emily's career matches in Human Resource Management and Training and Organizational Development, as well as her skills in Interpersonal Effectiveness.",
      },
      {
        id: "4d5d29ac-4186-4b92-ab92-c8609653d063",
        name: "Anna Boudinot",
        jobTitle: "Founder & CEO",
        reasoning:
          "Anna's role as Founder & CEO of Mustard & Moxie with experience in marketing strategy, branding, and storytelling connects with Emily's Entrepreneurship career match and her interest in Creative Production. and Skills with Analysis and Strategic Decision-Making.",
      },
      {
        id: "a7463546-21cc-47f2-8a4e-fc29688876b0",
        name: "Jorrel Sto Tomas",
        jobTitle: "Entrepreneur, Founder",
        reasoning:
          "Jorrel's role as Entrepreneur and Founder of an Early Stage Start Up, with experience in Data analytics/engineering and product management across multiple technology industries aligns with Emily's Career Matches such as Entrepreneurship, Strategic Planning and skills such as Analysis and Strategic Decision-Making. ",
      },
      {
        id: "3de40335-6ace-402d-8e47-0b60956f1332",
        name: "Lisa Risser",
        jobTitle: "Founder & CEO",
        reasoning:
          "Lisa's role as Founder & CEO of Jevan Consulting, with experience in financial and operational executive leadership, align well with Emily's career matches such as General Management, Management Consulting and skills such as Analysis and Strategic Decision-Making.",
      },
      {
        id: "2f3e472f-b7d7-42a6-a470-481b2f03853b",
        name: "Mariah Martin",
        jobTitle: "Director of Business Development, Sport",
        reasoning:
          "Mariah's role as Director of Business Development, Sport with experience in Operations, Project Management, and Business Development aligns with Emily's Skills and Career Matches. This advizer could be a great resource.",
      },
    ],
  };
}
